import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check, Leaf, Truck, ShieldCheck, Calendar as CalIcon, Sparkles } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const PROCESS = [
  { num: "01", title: "Pick your tier", desc: "Choose small, medium or large based on your home size." },
  { num: "02", title: "Pick your two weeks", desc: "Open the calendar and select the start of your 14-day window." },
  { num: "03", title: "We deliver", desc: "Stacked totes, dollies and supplies dropped at your door." },
  { num: "04", title: "We pick up", desc: "When your two weeks are up, we collect, sanitize and reuse." },
];

const FAQS = [
  { q: "How long is the rental?", a: "Every tier is a flat 14-day rental window. Pick the start date that suits you—we deliver at the start and pick up at the end." },
  { q: "Are the totes clean?", a: "Yes. Every tote is industrially sanitized between renters. Our reusable system replaces ~1,000 cardboard boxes per move." },
  { q: "What's the service area?", a: "We currently serve metro neighbourhoods within 40 miles of our depot. Enter your address at checkout and we'll confirm." },
  { q: "Can I extend my rental?", a: "Yes. Email wc2389@yahoo.com at least 48 hours before pickup—extensions are $9 per day." },
  { q: "What if I damage a tote?", a: "Light scuffs are expected and free. Cracked or missing totes are billed at $35 each. Dollies are similarly fair-use — replacement is $250. Damage Protection ($19) covers smaller incidents like broken lids, dolly wheels or straps, but does not waive the tote or dolly replacement fees." },
];

export default function Landing() {
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    api.get("/tiers").then((r) => setTiers(r.data)).catch(() => setTiers([]));
  }, []);

  return (
    <div className="bg-[#F8F8F8] text-[#0A0A0A] min-h-screen overflow-x-hidden">
      <Nav />

      {/* ======= HERO ======= */}
      <section className="relative pt-24 md:pt-28 pb-16" data-testid="hero-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-5 fade-up">
            <div className="flex items-center gap-2 mb-6">
              <span className="w-8 h-px bg-[#FF4F00]" />
              <span className="text-xs tracking-[0.25em] uppercase text-neutral-600 font-semibold">
                Reusable moving · 14-day rental
              </span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[0.95] tracking-tight">
              Move your home in <span className="text-[#FF4F00]">totes</span>, not boxes.
            </h1>
            <p className="mt-6 text-lg text-neutral-600 leading-relaxed max-w-md">
              Premium stackable totes, dollies and supplies — delivered to your
              door for a flat two-week window. Pack, move, unpack. We collect.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#tiers"
                data-testid="hero-primary-cta"
                className="group inline-flex items-center gap-3 bg-[#FF4F00] hover:bg-[#E64700] text-white px-7 py-4 font-semibold tracking-wide transition-colors"
              >
                Choose your tier
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="#how"
                data-testid="hero-secondary-cta"
                className="inline-flex items-center gap-3 border border-[#0A0A0A] text-[#0A0A0A] px-7 py-4 font-semibold tracking-wide hover:bg-[#0A0A0A] hover:text-white transition-colors"
              >
                How it works
              </a>
            </div>

            <div className="mt-12 flex items-center gap-8 text-sm text-neutral-600">
              <div className="flex items-center gap-2"><Leaf className="w-4 h-4 text-[#FF4F00]"/> Zero cardboard</div>
              <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-[#FF4F00]"/> Free delivery</div>
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#FF4F00]"/> Sanitized</div>
            </div>
          </div>

          <div className="lg:col-span-7 relative">
            <div className="relative w-full aspect-square lg:aspect-[7/6] bg-neutral-100 border border-neutral-200 overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_tote-rental-3d/artifacts/ghd5a02b_watermarked_img_7663699566869299296.jpg"
                alt="Cardboard boxes toppling next to a neat stack of ToteHaul reusable moving totes in a warehouse"
                className="w-full h-full object-cover"
                loading="eager"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
              <div className="absolute top-4 left-4 text-xs tracking-[0.25em] uppercase text-white/90 font-semibold drop-shadow">
                Boxes fail · Totes don&apos;t
              </div>
              <div className="absolute bottom-4 right-4 flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-white font-semibold drop-shadow">
                <Sparkles className="w-3 h-3"/> Premium stackable totes
              </div>
            </div>
          </div>
        </div>

        {/* marquee */}
        <div className="mt-20 border-y border-neutral-200 bg-white overflow-hidden">
          <div className="flex whitespace-nowrap scroll-marquee py-5">
            {["copy-a", "copy-b"].map((lane) => (
              <div key={lane} className="flex items-center gap-16 px-8 font-display text-2xl md:text-3xl font-bold text-neutral-900">
                {["Pack smarter", "Move greener", "Zero cardboard", "Stack · Lock · Roll", "14-day window", "Reused 8× per tote"].map((t) => (
                  <span key={`${lane}-${t}`} className="flex items-center gap-16">
                    <span>{t}</span>
                    <span className="w-2 h-2 bg-[#FF4F00] rounded-full"/>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======= TIERS ======= */}
      <section id="tiers" className="py-24 md:py-32" data-testid="tiers-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="text-xs tracking-[0.25em] uppercase text-neutral-500 font-semibold">01 · Pricing</span>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[0.95] tracking-tight max-w-2xl">
                Pack. Stack. Move. Done.
              </h2>
            </div>
            <p className="text-neutral-600 max-w-sm">
              Pick the bundle that matches your home. Each rental is a strict
              14-day window with free door-to-door delivery and pickup.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {tiers.map((t) => (
              <TierCard key={t.id} tier={t} />
            ))}
          </div>
        </div>
      </section>

      {/* ======= HOW IT WORKS ======= */}
      <section id="how" className="bg-white border-y border-neutral-200 py-24 md:py-32" data-testid="how-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <span className="text-xs tracking-[0.25em] uppercase text-neutral-500 font-semibold">02 · Process</span>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[0.95] tracking-tight">
                From sign-up to settled, in four steps.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <img
                src="https://customer-assets.emergentagent.com/job_tote-rental-3d/artifacts/vxxyphrd_44548445.webp"
                alt="Stacked black-and-yellow ToteHaul moving totes in a clean garage next to shelving of more totes"
                className="w-full h-full object-cover max-h-[420px]"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-neutral-200">
            {PROCESS.map((p) => (
              <div key={p.num} className="bg-white p-8 md:p-10 hover:bg-neutral-50 transition-colors">
                <span className="font-display text-5xl font-bold text-[#FF4F00]">{p.num}</span>
                <h3 className="font-display text-xl font-bold mt-6">{p.title}</h3>
                <p className="mt-3 text-neutral-600 leading-relaxed text-sm">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======= STATS ======= */}
      <section className="py-24 md:py-32" data-testid="stats-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-neutral-200 border border-neutral-200">
          {[
            { n: "8×", l: "Reuses per tote" },
            { n: "1,000", l: "Boxes replaced / move" },
            { n: "14", l: "Day flat window" },
            { n: "0", l: "Cardboard waste" },
          ].map((s) => (
            <div key={s.l} className="bg-[#F8F8F8] p-8 md:p-12">
              <p className="font-display text-5xl md:text-6xl font-bold text-[#0A0A0A]">{s.n}</p>
              <p className="mt-3 text-xs tracking-[0.25em] uppercase text-neutral-500 font-semibold">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ======= FAQ ======= */}
      <section id="faq" className="bg-white border-t border-neutral-200 py-24 md:py-32" data-testid="faq-section">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-16">
          <span className="text-xs tracking-[0.25em] uppercase text-neutral-500 font-semibold">03 · Questions</span>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[0.95] tracking-tight">
            Frequently asked.
          </h2>

          <Accordion type="single" collapsible className="mt-12">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`item-${i}`}
                className="border-b border-neutral-200"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="font-display text-lg md:text-xl font-bold text-left py-6 hover:no-underline hover:text-[#FF4F00]">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-neutral-600 leading-relaxed text-base pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ======= CTA STRIP ======= */}
      <section className="bg-[#FF4F00] text-white py-20" data-testid="cta-strip">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight max-w-2xl">
            Your two-week window is one click away.
          </h2>
          <a
            href="#tiers"
            data-testid="cta-strip-button"
            className="inline-flex items-center gap-3 bg-white text-[#0A0A0A] px-8 py-4 font-semibold tracking-wide hover:bg-[#0A0A0A] hover:text-white transition-colors"
          >
            Reserve your totes <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const TierCard = ({ tier }) => {
  const featured = tier.badge != null;
  return (
    <div
      data-testid={`tier-card-${tier.id}`}
      className={`relative flex flex-col p-8 md:p-10 border transition-all duration-300 hover:-translate-y-1 ${
        featured
          ? "bg-[#0A0A0A] text-white border-[#0A0A0A] shadow-xl"
          : "bg-white border-neutral-200 hover:shadow-md"
      }`}
    >
      {featured && (
        <span className="absolute -top-3 left-8 bg-[#FF4F00] text-white text-xs tracking-[0.2em] uppercase font-semibold px-3 py-1">
          {tier.badge}
        </span>
      )}
      <span className={`text-xs tracking-[0.25em] uppercase font-semibold ${featured ? "text-neutral-400" : "text-neutral-500"}`}>
        {tier.tagline}
      </span>
      <h3 className="font-display text-3xl md:text-4xl font-bold mt-3">{tier.name}</h3>

      <div className="mt-8 flex items-baseline gap-2">
        <span className="font-display text-5xl md:text-6xl font-bold">${tier.price}</span>
        <span className={`text-sm ${featured ? "text-neutral-400" : "text-neutral-500"}`}>/ 2 weeks</span>
      </div>

      <p className={`mt-3 text-sm ${featured ? "text-neutral-300" : "text-neutral-600"}`}>
        {tier.tote_count} totes · {tier.duration_days}-day window
      </p>

      <p className={`mt-2 text-xs tracking-[0.15em] uppercase font-semibold ${featured ? "text-[#FF4F00]" : "text-[#FF4F00]"}`}>
        ${tier.deposit} deposit today · balance auto-charged on delivery
      </p>

      <ul className="mt-8 space-y-3 flex-1">
        {tier.includes.map((inc) => (
          <li key={inc} className="flex items-start gap-3 text-sm">
            <Check className={`w-4 h-4 mt-0.5 shrink-0 ${featured ? "text-[#FF4F00]" : "text-[#FF4F00]"}`} />
            <span className={featured ? "text-neutral-200" : "text-neutral-700"}>{inc}</span>
          </li>
        ))}
      </ul>

      <Link
        to={`/book/${tier.id}`}
        data-testid={`tier-select-${tier.id}`}
        className={`mt-10 inline-flex items-center justify-between gap-3 px-6 py-4 font-semibold tracking-wide transition-colors ${
          featured
            ? "bg-[#FF4F00] text-white hover:bg-[#E64700]"
            : "bg-[#0A0A0A] text-white hover:bg-[#FF4F00]"
        }`}
      >
        <span className="inline-flex items-center gap-2"><CalIcon className="w-4 h-4"/> Pick your 2 weeks</span>
        <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
};
